import React, { useState, useEffect } from 'react';
import { X, Check, Edit } from 'lucide-react';
import type { UserGroup } from '../../../../../types/userRole';

interface ViewGroupModalProps {
  isOpen: boolean;
  group: UserGroup | null;
  onClose: () => void;
  onSave?: (groupId: number, newName: string) => void;
}

const ViewGroupModal: React.FC<ViewGroupModalProps> = ({
  isOpen,
  group,
  onClose,
  onSave
}) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedGroupName, setEditedGroupName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (group) {
      setEditedGroupName(group.name);
    }
  }, [group]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleClose = () => {
    setIsEditMode(false);
    setEditedGroupName(group?.name || '');
    onClose();
  };

  const handleEditDetails = () => {
    setIsEditMode(true);
  };

  const handleCancel = () => {
    setIsEditMode(false);
    setEditedGroupName(group?.name || '');
  };

  const handleSaveChanges = async () => {
    if (!group || !editedGroupName.trim()) return;
    
    setIsLoading(true);
    try {
      if (onSave) {
        await onSave(group.id, editedGroupName.trim());
      }
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving group:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGroupNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedGroupName(e.target.value);
  };

  if (!isOpen || !group) return null;

  // Get the primary permission for display
  const primaryPermission = group.permissions[0];
  const permissionLevel = primaryPermission?.name || 'No Access';
  const isFullAccess = primaryPermission?.type === 'full-access';

  return (
    <div className="view-group-modal-backdrop" onClick={handleBackdropClick}>
      <div className="view-group-modal-container">  
        {/* Header Section */}
        <div className="view-group-modal-header">
          <div className="view-group-modal-header-content">
            <h2 className="view-group-modal-title">Group Details</h2>
            <p className="view-group-modal-subtitle">Manage group information</p>
          </div>
          <button
            className="view-group-modal-close"
            onClick={handleClose}
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content Section */}
        <div className="view-group-modal-body">
          {/* Form Fields Section */}
          <div className="view-group-form-section">
            <div className="view-group-field">
              <label className="view-group-field-label">Group ID</label>
              <input
                type="text"
                value={group.id.toString()}
                readOnly
                className="view-group-field-input readonly"
              />
            </div>
            
            <div className="view-group-field">
              <label className="view-group-field-label">Group Name</label>
              <input
                type="text"
                value={editedGroupName}
                onChange={handleGroupNameChange}
                readOnly={!isEditMode}
                className={`view-group-field-input ${!isEditMode ? 'readonly' : 'editable'}`}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Summary Cards Section */}
          <div className="view-group-summary-section">
            <div className="view-group-summary-card members">
              <div className="view-group-summary-number">{group.memberCount}</div>
              <div className="view-group-summary-label">Members</div>
              <div className="view-group-summary-underline"></div>
            </div>
            
            <div className="view-group-summary-card permission">
              <div className="view-group-permission-content">
                {isFullAccess && (
                  <div className="view-group-permission-check">
                    <Check size={16} />
                  </div>
                )}
                <div className="view-group-permission-badge">
                  {permissionLevel}
                </div>
              </div>
              <div className="view-group-permission-label">Permission Level</div>
            </div>
          </div>
        </div>

        {/* Footer Section */}
        <div className="view-group-modal-footer">
          {!isEditMode ? (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="view-group-btn-secondary"
                disabled={isLoading}
              >
                Back to List
              </button>
              <button
                type="button"
                onClick={handleEditDetails}
                className="view-group-btn-primary"
                disabled={isLoading}
              >
                <Edit size={16} />
                Edit Details
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleCancel}
                className="view-group-btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveChanges}
                className="view-group-btn-primary"
                disabled={isLoading || !editedGroupName.trim()}
              >
                <Edit size={16} />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewGroupModal;